import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FaApple as FaAppleIcon, FaGooglePlay as FaGooglePlayIcon } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";

const FaApple = FaAppleIcon as React.ElementType;
const FaGooglePlay = FaGooglePlayIcon as React.ElementType;

interface AppEntry {
  name: string;
  url: string;
  icon: React.ElementType;
  iconClass: string;
}

interface SectionProps {
  title: string;
  apps: AppEntry[];
}

const Section: React.FC<SectionProps> = ({ title, apps }) => (
  <div className="mb-5">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{title}</h3>
    <div className="flex flex-col gap-2">
      {apps.map((app) => (
        <a
          key={app.url}
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <app.icon className={`text-2xl shrink-0 ${app.iconClass}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">{app.name}</p>
            <p className="text-xs text-gray-400 truncate">{new URL(app.url).hostname}</p>
          </div>
          <QRCodeSVG value={app.url} size={56} className="shrink-0 rounded" />
        </a>
      ))}
    </div>
  </div>
);

const TESTFLIGHT_APPS: AppEntry[] = [
  { name: "Myovolt Research", url: "https://testflight.apple.com/join/qs8nDVv7", icon: FaApple, iconClass: "text-blue-500" },
  { name: "Back Recover",     url: "https://testflight.apple.com/join/8E01w90j", icon: FaApple, iconClass: "text-blue-500" },
];

const APPSTORE_APPS: AppEntry[] = [
  { name: "Myovolt Research", url: "https://apps.apple.com/nz/app/myovolt-research/id6680148204", icon: FaApple, iconClass: "text-gray-800" },
  { name: "Back Recover",     url: "https://apps.apple.com/nz/app/back-recover/id6479965816",     icon: FaApple, iconClass: "text-gray-800" },
];

const GOOGLEPLAY_APPS: AppEntry[] = [
  { name: "Myovolt Research", url: "https://play.google.com/store/apps/details?id=com.myovolt_2.myovolt_researcher&hl=en", icon: FaGooglePlay, iconClass: "text-green-500" },
  { name: "Back Recover",     url: "https://play.google.com/store/apps/details?id=com.myovolt_2.myovolt_2&hl=en",           icon: FaGooglePlay, iconClass: "text-green-500" },
];

const AppsModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <li>
        <a href="#" onClick={(e) => { e.preventDefault(); setIsOpen(true); }}>
          Apps
        </a>
      </li>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
            <div className="relative bg-white p-6 md:p-8 rounded-3xl shadow-2xl w-11/12 max-w-lg max-h-[85vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6">Apps</h2>
              <button
                className="btn btn-sm btn-circle absolute right-4 top-4"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>

              <Section title="TestFlight" apps={TESTFLIGHT_APPS} />
              <Section title="App Store"  apps={APPSTORE_APPS} />
              <Section title="Google Play" apps={GOOGLEPLAY_APPS} />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AppsModal;
